import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import type { DynamicBundleAdjustmentState } from "@redotech/redo-return-core-common/return-flow-step-valid";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";

export const DynamicBundleAdjustmentDetails = memo(
  function DynamicBundleAdjustmentDetails({
    stepSelect,
    state,
    setState,
    flowType,
  }: StepTypeDetailsProps<DynamicBundleAdjustmentState>) {
    const { t } = useTranslation();
    const nextStepChange = useHandler((next: StepId | undefined) => {
      setState((state) => ({ ...state, next }));
    });

    return (
      <Flex dir="column" gap="lg">
        <Flex dir="column" gap="sm">
          <Text>
            {t(
              "When customers purchase items as a bundle at a discounted price and then return only part of that bundle, this step automatically adjusts their refund to remove the bundle discount proportionally. This prevents customers from keeping high-value items at the discounted bundle price while returning low-value items for a full refund.",
            )}
          </Text>

          <Text fontWeight="semibold">{t("Example:")}</Text>
          <Text>
            {t(
              "Customer buys a bundle for $100 (retail value $120) containing:",
            )}
          </Text>
          <Flex dir="column" gap="xs" pl="md">
            <Text>
              {t("• Product A: $30 retail → $25 proportional bundle price")}
            </Text>
            <Text>
              {t("• Product B: $50 retail → $41.67 proportional bundle price")}
            </Text>
            <Text>
              {t("• Product C: $40 retail → $33.33 proportional bundle price")}
            </Text>
          </Flex>
          <Text fontSize="sm" textColor="secondary">
            {t(
              "Note: The adjustment accounts for any items previously returned from the same bundle to ensure accurate calculations across multiple returns.",
            )}
          </Text>
        </Flex>

        <Text fontSize="xs" textColor="secondary">
          {t("Next step")}
        </Text>
        {stepSelect({ value: state.next, valueChange: nextStepChange })}
      </Flex>
    );
  },
);
